import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';
import util from '~/lib/util/util';
import { getHtmlText } from '~/lib/util/patient.util';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { FieldErrorState } from '~/components/maud/patients/note/EditNoteForm';
import { Dispatch, MutableRefObject } from 'react';
import { EditorV2State } from '@equip.health/ui';
import { FormikProps } from 'formik';

type ValidateArgs = {
  formValues: EditNoteFormData;
  updateFieldErrorReducer: Dispatch<FieldErrorState>;
  noteContentEditorRef: MutableRefObject<EditorV2State | null>;
  sessionSummaryEditorRef?: MutableRefObject<EditorV2State | null>;
  isAddendumModal?: boolean;
  shouldValidateSessionSummary?: boolean;
};

type SessionSummaryArgs = Pick<
  ValidateArgs,
  'formValues' | 'sessionSummaryEditorRef' | 'shouldValidateSessionSummary'
>;

export const getSessionSummaryError = ({
  formValues,
  sessionSummaryEditorRef,
  shouldValidateSessionSummary,
}: SessionSummaryArgs): string | null => {
  const hasNoteTemplate =
    !!formValues?.noteTemplateRevision?.node &&
    formValues.noteTemplateRevision.node !== NO_NOTE_TEMPLATE_OPTION.node;

  if (!shouldValidateSessionSummary || !hasNoteTemplate) {
    return null;
  }

  if (sessionSummaryEditorRef?.current) {
    return sessionSummaryEditorRef.current.getText().trim().length === 0
      ? 'Session summary is required'
      : null;
  }
  // Legacy textarea path passes whitespace-only summaries; retires with the
  // sessionSummaryRichText flag.
  return !formValues.sessionSummary ? 'Session summary is required' : null;
};

// to validate mandatory field before signing
export const validateEditNoteFormFields = ({
  formValues,
  updateFieldErrorReducer,
  noteContentEditorRef,
  sessionSummaryEditorRef,
  isAddendumModal = false,
  shouldValidateSessionSummary = false,
}: ValidateArgs): boolean => {
  if (!formValues) {
    return false;
  }
  const titleError = util.getEmptyStringError(formValues.title, 'Title');
  const dateError = util.getEmptyStringError(
    formValues.activityDate?.toString(),
    'Activity Date',
  );

  let editorError: string | null;

  if (!isAddendumModal) {
    const text = noteContentEditorRef.current?.getText();
    editorError = text?.trim().length === 0 ? 'Note body is required.' : null;
  } else {
    editorError =
      getHtmlText(formValues.addendumContent ?? '').length === 0
        ? 'Note body is required.'
        : null;
  }

  const hasNoteTemplate =
    !!formValues.noteTemplateRevision?.node &&
    formValues.noteTemplateRevision?.node !== NO_NOTE_TEMPLATE_OPTION.node;
  if (hasNoteTemplate && !isAddendumModal) {
    // we expect notes with a template to have empty noteContent
    editorError = null;
  }

  const typeError = !formValues.type ? 'Note type is required' : null;

  const summaryError = getSessionSummaryError({
    formValues,
    sessionSummaryEditorRef,
    shouldValidateSessionSummary,
  });

  updateFieldErrorReducer({
    dateError,
    editorError,
    titleError,
    typeError,
    summaryError,
    carePlanError: null,
  });

  return Boolean(
    titleError || editorError || typeError || dateError || summaryError,
  );
};

export const validateEditNoteFormFieldsFromFormikRef = ({
  formikRef,
  isAddendumModal = false,
  noteContentEditorRef,
  updateFieldErrorReducer,
}: Omit<ValidateArgs, 'formValues'> & {
  formikRef: MutableRefObject<FormikProps<EditNoteFormData>>;
}): boolean => {
  if (formikRef?.current?.values) {
    const { values } = formikRef.current;
    return validateEditNoteFormFields({
      formValues: values,
      isAddendumModal,
      noteContentEditorRef,
      updateFieldErrorReducer,
    });
  }
  return false;
};
